import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import BlogLayout from "../../../components/BlogLayout";
import Venkat from "../../../assets/venkat.jpg";
import AIJudiciary from "../../../assets/AIJudiciary.webp";
import AuthorCard from "../../../components/AuthorCard";
import { FaTwitter, FaLinkedin } from "react-icons/fa";
import "./GraphSummit25.css";

const GraphSummit25 = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulating data fetch
    const fetchBlog = async () => {
      try {
        // In a real app, this would be an API call
        const mockBlog = {
          title: "Neo4j's Graph Summit 2025 Copenhagen",
          publishDate: "April 01, 2025",
          author: {
            name: "Venkatesan R",
            image: Venkat,
            bio: "<p>With 11 years of experience in AI, spanning presales, solutions, and delivery, I’ve worked at the intersection of technology and business—leading AI solutions teams, developing accelerators, and crafting strategies that bridge technical capabilities with real-world needs. Managing AI projects has given me valuable insights into anticipating challenges and refining solutions to better meet customer needs.</p><p>Thought Box is my space to explore ideas beyond technology—where different perspectives meet, and curiosity leads the way. Currently residing in Copenhagen, I enjoy theatre, playing badminton, and contributing to performing arts by designing lights for shows.</p>",
            socialLinks: [
              {
                icon: <FaLinkedin size={24} style={{ color: "#0077B5" }} />,
                url: "https://www.linkedin.com/in/venkatesan-r-96061b69/",
              },
            ],
          },
          content: "",
        };
        setBlog(mockBlog);
      } catch (error) {
        console.error("Error fetching blog:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  if (loading) {
    return (
      <BlogLayout title="Loading...">
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </BlogLayout>
    );
  }

  if (!blog) {
    return (
      <BlogLayout title="Error">
        <div className="error-container">
          <h2 className="error-title">Blog post not found</h2>
          <p className="error-message">
            The requested blog post could not be loaded.
          </p>
        </div>
      </BlogLayout>
    );
  }

  return (
    <BlogLayout title={blog.title} customClass="blog-header">
      <Helmet>
        <title>
          Graph Summit 2025: Unveiling the Power of Knowledge Graphs
        </title>
        <meta
          name="description"
          content="Explore how Neo4j's Graph Summit 2025 showcased real-world applications of knowledge graphs in AI, digital twins, and large-scale data modeling."
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Neo4j's Graph Summit 2025 Copenhagen" />
        <meta
          property="og:description"
          content="Explore how Neo4j's Graph Summit 2025 showcased real-world applications of knowledge graphs in AI, digital twins, and large-scale data modeling."
        />
        <meta property="og:image" content="https://thought-box.in/og-images/GraphSummit.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:url"
          content="https://thought-box.in/blog/graph-summit-25"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Neo4j's Graph Summit 2025 Copenhagen"
        />
        <meta
          name="twitter:description"
          content="Explore how Neo4j's Graph Summit 2025 showcased real-world applications of knowledge graphs in AI, digital twins, and large-scale data modeling."
        />
        <meta name="twitter:image" content="https://thought-box.in/og-images/GraphSummit.webp" />
      </Helmet>

      <article className="article-content">
        <h1 className="blog-article-title">{blog.title}</h1>

        <div className="author-meta">
          <img
            src={blog.author.image}
            alt={blog.author.name}
            className="author-image"
          />
          <div className="author-info">
            <p className="author-name">{blog.author.name}</p>
            <p className="publish-date">{blog.publishDate}</p>
          </div>
        </div>

        <div className="blog-article-body">
          {blog.content}
          <body>
            <section>
              <p>
                For the past three years, attending the Graph Summit has been a
                tradition, each time deepening my understanding of knowledge
                graphs (KGs). This year’s event in Copenhagen was no exception,
                showcasing real-world applications, hands-on workshops, and
                groundbreaking innovations in AI, digital twins, and large-scale
                data modeling.
              </p>
            </section>

            <section>
              <h2>Why Knowledge Graphs Matter</h2>
              <p>
                Unlike traditional databases, knowledge graphs connect data
                points through relationships, providing richer context for
                decision-making. Google’s Knowledge Graph is a prime
                example—enhancing search by understanding entities rather than
                just keywords.
              </p>
              <p>
                Industries like fraud detection, recommendation engines,
                healthcare, and supply chain optimization are leveraging KGs to
                uncover hidden insights and drive smarter decisions.
              </p>
            </section>

            <section>
              <h2>Graph Summit 2025</h2>
              <h3>Event Structure</h3>
              <ul>
                <li>
                  <strong>Morning Sessions</strong> – Five talks, including two
                  by Neo4j experts, real-world case studies, and a panel
                  discussion.
                </li>
                <li>
                  <strong>Afternoon Workshops</strong> – Hands-on coding
                  sessions, allowing participants to explore Neo4j’s
                  capabilities.
                </li>
              </ul>

              <h2>Customer Success stories</h2>

              <article>
                <h4> 1) Novo Nordisk’s GraphRAG for Biomedical Research</h4>
                <p>
                  Novo Nordisk demonstrated how they use GraphRAG to index
                  biomedical literature and patents for drug discovery.
                </p>

                <h5>How It Works:</h5>
                <ul>
                  <li>
                    Extracts structured knowledge from unstructured biomedical
                    texts (papers, patents).
                  </li>
                  <li>
                    Uses entity recognition and relationship extraction to build
                    a KG.
                  </li>
                  <li>
                    Supports an AI-powered conversational search for research
                    insights.
                  </li>
                </ul>

                <h5>Why It’s Groundbreaking:</h5>
                <ul>
                  <li>
                    Faster discovery of relationships between research papers.
                  </li>
                  <li>Reduces AI hallucinations common in GenAI models.</li>
                  <li>
                    Enables multi-hop reasoning, improving search accuracy.
                  </li>
                </ul>

                <p>
                  With GraphRAG, scientists can ask, “Which studies support this
                  drug’s effectiveness based on past clinical trials?” and
                  receive precise, contextual answers.
                </p>
              </article>

              <article>
                <h4>2) Green Cargo’s Digital Twin for Freight Operations</h4>
                <p>
                  Managing freight logistics is complex, with multiple transfers
                  and last-minute disruptions. Green Cargo built a graph-based
                  digital twin to optimize operations in real time.
                </p>

                <h5>How Neo4j Helps:</h5>
                <ul>
                  <li>Models train schedules, locations, and dependencies.</li>
                  <li>
                    Dynamically reroutes shipments when disruptions occur.
                  </li>
                  <li>
                    Uses predictive analytics to prevent cascading delays.
                  </li>
                </ul>

                <h5>Key Graph Capabilities:</h5>
                <ul>
                  <li>Shortest path calculations for rerouting.</li>
                  <li>Real-time decision support.</li>
                  <li>Network analysis for optimizing logistics.</li>
                </ul>

                <p>
                  By leveraging Neo4j, Green Cargo has improved efficiency and
                  reduced downtime.
                </p>
              </article>
            </section>

            <section>
              <h2>Neo4j’s Product Vision & Roadmap</h2>
              <p>Neo4j’s roadmap focuses on:</p>
              <ul>
                <li>
                  <strong>Cloud-first strategy</strong> – Now available on AWS,
                  Azure, and GCP, with AWS Marketplace integration.
                </li>
                <li>
                  <strong>Ease of use</strong> – Neo Dash (low-code dashboard
                  builder), simplified data imports.
                </li>
                <li>
                  <strong>AI & GenAI Integration</strong> – Supports frameworks
                  like LangChain, LlamaIndex, and introduces the GraphRAG Python
                  package.
                </li>
              </ul>
            </section>

            <section>
              <h2> My Takeaways</h2>
              <h3>1. AuroDB is free - Ideal for PoCs</h3>
              <p>
                If you’re new to Neo4j, AuraDB Free is an excellent option to
                start pilot projects without upfront costs.
              </p>
              <h3>2. Cloud Adoption</h3>
              <p>
                As a longtime Neo4j advocate, I was curious about its adoption
                hurdles in cloud environments. Many enterprises streamline cloud
                adoption by provisioning resources directly from their cloud
                provider’s console—favoring managed services over custom
                deployments, at least for PoCs/pilot projects.
              </p>

              <h5>A Question I Asked during the panel discussion:</h5>
              <p>
                "Will Neo4j be available as a pay-as-you-go model for easier
                cloud adoption?"
              </p>

              <p>
                The answer was yes, which is a big step forward. However, a key
                limitation remains:
              </p>

              <p>
                <strong>
                  {" "}
                  Can Neo4j be enabled directly from the AWS console?
                </strong>
              </p>
              <p>
                <strong>No.</strong> It must be subscribed via AWS Marketplace
                and enabled through Neo4j Cloud.
              </p>

              <h5>Why This Matters:</h5>
              <ul>
                <li>
                  Neo4j’s billing integrates with AWS, but the service does not
                  run within the customer’s AWS account.
                </li>
                <li>
                  Many enterprises require special approvals for AWS Marketplace
                  services, delaying adoption.
                </li>
                <li>
                  As a result, Neo4j risks being overlooked in favor of
                  instantly deployable databases like RDS, Neptune, or DynamoDB.
                </li>
              </ul>

              <h5>The Ideal Future:</h5>
              <p>"When can we enable a graph database as easily as RDS?"</p>
              <p>
                If Neo4j could be deployed directly via the AWS console, it
                would remove friction and accelerate enterprise adoption.
              </p>
            </section>

            <section>
              <h3>3. When Does Hybrid GraphRAG Add Value?</h3>
              <p>I’ve explored an important question:</p>
              <p> Does Hybrid GraphRAG offer real value over baseline RAG?</p>

              <h3>Baseline RAG vs. Hybrid GraphRAG</h3>
              <ul>
                <li>
                  <strong>Baseline RAG</strong> retrieves relevant text chunks
                  via vector search (semantic similarity).
                </li>
                <li>
                  <strong>Hybrid GraphRAG</strong> combines vector search with
                  structured relationships to enhance retrieval.
                </li>
              </ul>

              <h3>When Hybrid GraphRAG Makes Sense:</h3>
              <ul>
                <li>
                  Your data has inherent structure – Example: Drug discovery,
                  where explicit relationships (e.g., "Drug X inhibits Protein
                  Y") enable multi-hop reasoning.
                </li>
                <li>
                  Multi-hop answering is critical – Example: Legal document
                  analysis, where regulations reference multiple sources.
                </li>
                <li>
                  Structured knowledge retrieval improves accuracy – Example:
                  AI-powered customer support, where relationships between
                  product versions and troubleshooting guides refine responses.
                </li>
              </ul>

              <h3>When to Stick to Baseline RAG:</h3>
              <ul>
                <li>
                  If you rely solely on vector search – A graph layer adds no
                  value.
                </li>
                <li>
                  If your data lacks meaningful structure – Graphs are
                  unnecessary for unconnected documents.
                </li>
              </ul>

              <h5>Key Takeaway:</h5>
              <p>
                Use Hybrid GraphRAG when relationships between entities matter;
                otherwise, stick to vector search.
              </p>
            </section>

            <section>
              <h2>Final Thoughts</h2>
              <p>
                Graph Summit 2025 reinforced that graphs are more than
                databases—they’re engines of discovery, optimization, and
                intelligence. Whether accelerating drug research, optimizing
                logistics, or enhancing AI capabilities, graph technology is
                shaping the future.
              </p>
              <p>
                If you're new to Neo4j, try AuraDB Free and explore GraphRAG
                firsthand!
              </p>
              <p>
                Would love to hear your thoughts—how are you using graphs in
                your projects?
              </p>
            </section>
          </body>
        </div>

        <AuthorCard author={blog.author} />
      </article>
    </BlogLayout>
  );
};

export default GraphSummit25;
